import React from "react";
import { useI18n } from "@/i18n";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const GRIND_OPTIONS = [
  { value: "extra-fine", en: "Extra fine (powder)", ar: "ناعم جداً (بودرة)" },
  { value: "fine", en: "Fine", ar: "ناعم" },
  { value: "fine-medium", en: "Fine–medium", ar: "ناعم إلى متوسط" },
  { value: "medium-fine", en: "Medium–fine", ar: "ناعم متوسط" },
  { value: "medium", en: "Medium", ar: "متوسط" },
  { value: "medium-coarse", en: "Medium–coarse", ar: "خشن متوسط" },
  { value: "coarse", en: "Coarse", ar: "خشن" },
  { value: "extra-coarse", en: "Extra coarse", ar: "خشن جداً" },
];

// Sensible defaults per brewing method (used when user picks/changes method)
export const METHOD_DEFAULTS = {
  v60:         { dose_g: 20, yield_g: 320, ratio: 16,  temp_c: 94, time: "2:45", grind: "medium-fine", bloom_g: 40, bloom_s: 30 },
  chemex:      { dose_g: 30, yield_g: 510, ratio: 17,  temp_c: 95, time: "4:30", grind: "medium-coarse", bloom_g: 60, bloom_s: 45 },
  french_press:{ dose_g: 30, yield_g: 450, ratio: 15,  temp_c: 94, time: "4:00", grind: "coarse" },
  aeropress:   { dose_g: 16, yield_g: 220, ratio: 14,  temp_c: 85, time: "1:30", grind: "fine-medium" },
  espresso:    { dose_g: 18, yield_g: 36,  ratio: 2,   temp_c: 93, time: "0:28", grind: "fine", pressure_bar: 9 },
  moka:        { dose_g: 18, yield_g: 180, ratio: 10,  temp_c: 100, time: "4:30", grind: "fine-medium" },
  cold_brew:   { dose_g: 60, yield_g: 480, ratio: 8,   temp_c: 22, time: "12:00:00", grind: "coarse" },
  turkish:     { dose_g: 7,  yield_g: 80,  ratio: 12,  temp_c: 90, time: "3:30", grind: "extra-fine" },
  iced_v60:    { dose_g: 20, yield_g: 200, ratio: 16,  temp_c: 94, time: "2:45", grind: "medium-fine", bloom_g: 40, bloom_s: 30, ice_g: 130 },
  iced_drip:   { dose_g: 50, yield_g: 500, ratio: 10,  temp_c: 4,  time: "4:00:00", grind: "medium", ice_g: 200 },
};

export const blankParams = () => ({
  dose_g: "", yield_g: "", ratio: "", temp_c: "",
  time: "", grind: "", bloom_g: "", bloom_s: "", pressure_bar: "",
  ice_g: "", pours: [], notes: "",
});

export const paramsFromDefaults = (method) => {
  const d = METHOD_DEFAULTS[method] || {};
  return {
    dose_g: d.dose_g ?? "",
    yield_g: d.yield_g ?? "",
    ratio: d.ratio ?? "",
    temp_c: d.temp_c ?? "",
    time: d.time ?? "",
    grind: d.grind ?? "",
    bloom_g: d.bloom_g ?? "",
    bloom_s: d.bloom_s ?? "",
    pressure_bar: d.pressure_bar ?? "",
    ice_g: d.ice_g ?? "",
    pours: [],
    notes: "",
  };
};

// Convert a stringy params object into the API payload (only sends non-empty values)
export const paramsToPayload = (p) => {
  if (!p) return null;
  const out = {};
  const numKeys = ["dose_g", "yield_g", "ratio", "temp_c", "bloom_g", "bloom_s", "pressure_bar", "ice_g"];
  for (const k of numKeys) {
    const v = p[k];
    if (v !== "" && v !== null && v !== undefined && !Number.isNaN(Number(v))) {
      out[k] = Number(v);
    }
  }
  for (const k of ["time", "grind", "notes"]) {
    const v = p[k];
    if (v && String(v).trim()) out[k] = String(v).trim();
  }
  if (Array.isArray(p.pours) && p.pours.length > 0) {
    const cleaned = p.pours
      .map((s) => ({
        ml: s.ml !== "" && s.ml != null && !Number.isNaN(Number(s.ml)) ? Number(s.ml) : null,
        at: s.at && String(s.at).trim() ? String(s.at).trim() : null,
      }))
      .filter((s) => s.ml || s.at);
    if (cleaned.length) out.pours = cleaned;
  }
  return Object.keys(out).length ? out : null;
};

export const paramsFromRecipe = (r) => {
  if (!r?.params) return blankParams();
  const p = r.params;
  return {
    dose_g: p.dose_g ?? "",
    yield_g: p.yield_g ?? "",
    ratio: p.ratio ?? "",
    temp_c: p.temp_c ?? "",
    time: p.time ?? "",
    grind: p.grind ?? "",
    bloom_g: p.bloom_g ?? "",
    bloom_s: p.bloom_s ?? "",
    pressure_bar: p.pressure_bar ?? "",
    ice_g: p.ice_g ?? "",
    pours: Array.isArray(p.pours)
      ? p.pours.map((s) => ({ ml: s.ml ?? "", at: s.at ?? "" }))
      : [],
    notes: p.notes ?? "",
  };
};

export default function RecipeParamsForm({ method, params, onChange, testIdPrefix = "params" }) {
  const { t, lang, isRTL } = useI18n();
  const isEspresso = method === "espresso";
  const isIced = method === "iced_v60" || method === "iced_drip";
  const showBloom = ["v60", "chemex", "aeropress", "iced_v60"].includes(method);

  const set = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    onChange({ ...params, [k]: v });
  };

  const pours = Array.isArray(params.pours) ? params.pours : [];
  const setPour = (idx, key, v) => {
    const next = pours.map((p, i) => (i === idx ? { ...p, [key]: v } : p));
    onChange({ ...params, pours: next });
  };
  const addPour = () => onChange({ ...params, pours: [...pours, { ml: "", at: "" }] });
  const removePour = (idx) => onChange({ ...params, pours: pours.filter((_, i) => i !== idx) });

  // Auto-compute ratio whenever dose & yield change
  React.useEffect(() => {
    const d = parseFloat(params.dose_g);
    const y = parseFloat(params.yield_g);
    if (d > 0 && y > 0) {
      const calc = Math.round((y / d) * 100) / 100;
      if (String(calc) !== String(params.ratio)) {
        onChange({ ...params, ratio: calc });
      }
    }
  }, [params.dose_g, params.yield_g, params, onChange]);

  return (
    <div
      className="rounded-xl border border-[#C86240]/20 bg-[#C86240]/5 p-5"
      data-testid={`${testIdPrefix}-card`}
    >
      <div className="flex items-baseline justify-between mb-1">
        <div className="text-sm font-semibold text-[#2A2421]">{t.addRecipe.paramsTitle}</div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C544F]">
          {isRTL ? "أرقام جاهزة — عدّل فقط" : "Pre-filled — just adjust"}
        </div>
      </div>
      <p className="text-xs text-[#5C544F] mb-5">{t.addRecipe.paramsLead}</p>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <ParamField label={t.addRecipe.dose}>
          <Input
            type="number" inputMode="decimal" step="0.1"
            value={params.dose_g} onChange={set("dose_g")}
            data-testid={`${testIdPrefix}-dose`}
            className="h-10 rounded-lg border-[#2A2421]/15"
          />
        </ParamField>
        <ParamField label={t.addRecipe.yield_}>
          <Input
            type="number" inputMode="decimal" step="0.1"
            value={params.yield_g} onChange={set("yield_g")}
            data-testid={`${testIdPrefix}-yield`}
            className="h-10 rounded-lg border-[#2A2421]/15"
          />
        </ParamField>
        <ParamField label={t.addRecipe.ratio}>
          <Input
            type="number" inputMode="decimal" step="0.01"
            value={params.ratio} onChange={set("ratio")}
            data-testid={`${testIdPrefix}-ratio`}
            className="h-10 rounded-lg border-[#2A2421]/15"
          />
        </ParamField>
        <ParamField label={t.addRecipe.temp}>
          <Input
            type="number" inputMode="decimal" step="1"
            value={params.temp_c} onChange={set("temp_c")}
            data-testid={`${testIdPrefix}-temp`}
            className="h-10 rounded-lg border-[#2A2421]/15"
          />
        </ParamField>
        <ParamField label={t.addRecipe.time}>
          <Input
            value={params.time} onChange={set("time")}
            placeholder={isRTL ? "2:45" : "2:45"}
            data-testid={`${testIdPrefix}-time`}
            className="h-10 rounded-lg border-[#2A2421]/15"
          />
        </ParamField>
        <ParamField label={t.addRecipe.grind}>
          <Select value={params.grind || ""} onValueChange={(v) => onChange({ ...params, grind: v })}>
            <SelectTrigger className="h-10 rounded-lg border-[#2A2421]/15" data-testid={`${testIdPrefix}-grind`}>
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              {GRIND_OPTIONS.map((g) => (
                <SelectItem key={g.value} value={g.value}>
                  {lang === "ar" ? g.ar : g.en}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </ParamField>

        {showBloom && (
          <>
            <ParamField label={t.addRecipe.bloomG}>
              <Input
                type="number" inputMode="decimal" step="0.1"
                value={params.bloom_g} onChange={set("bloom_g")}
                data-testid={`${testIdPrefix}-bloom-g`}
                className="h-10 rounded-lg border-[#2A2421]/15"
              />
            </ParamField>
            <ParamField label={t.addRecipe.bloomS}>
              <Input
                type="number" inputMode="decimal" step="1"
                value={params.bloom_s} onChange={set("bloom_s")}
                data-testid={`${testIdPrefix}-bloom-s`}
                className="h-10 rounded-lg border-[#2A2421]/15"
              />
            </ParamField>
          </>
        )}

        {isEspresso && (
          <ParamField label={t.addRecipe.pressure}>
            <Input
              type="number" inputMode="decimal" step="0.1"
              value={params.pressure_bar} onChange={set("pressure_bar")}
              data-testid={`${testIdPrefix}-pressure`}
              className="h-10 rounded-lg border-[#2A2421]/15"
            />
          </ParamField>
        )}

        {isIced && (
          <ParamField label={isRTL ? "كمية الثلج (جرام)" : "Ice (g)"}>
            <Input
              type="number" inputMode="decimal" step="1"
              value={params.ice_g} onChange={set("ice_g")}
              data-testid={`${testIdPrefix}-ice`}
              className="h-10 rounded-lg border-[#2A2421]/15"
            />
          </ParamField>
        )}
      </div>

      {/* Pours editor */}
      <div className="mt-5 rounded-lg border border-[#2A2421]/10 bg-white/60 p-4" data-testid={`${testIdPrefix}-pours-block`}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#5C544F] font-semibold">
              {isRTL ? "الصبّات" : "Pours"}
            </div>
            <div className="text-[11px] text-[#5C544F] mt-0.5">
              {isRTL ? "اختياري — أضف كل صبّة (مل + التوقيت)" : "Optional — list each pour (ml + timestamp)"}
            </div>
          </div>
          <button
            type="button"
            onClick={addPour}
            data-testid={`${testIdPrefix}-add-pour`}
            className="text-xs font-medium text-[#C86240] hover:text-[#b15637] px-3 py-1.5 rounded-md border border-[#C86240]/30 hover:border-[#C86240]"
          >
            + {isRTL ? "أضف صبّة" : "Add pour"}
          </button>
        </div>
        {pours.length === 0 ? (
          <div className="text-xs text-[#5C544F] italic">
            {isRTL ? "لا توجد صبّات بعد." : "No pours added yet."}
          </div>
        ) : (
          <div className="space-y-2">
            {pours.map((p, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`${testIdPrefix}-pour-row-${idx}`}>
                <div className="col-span-1 text-xs font-semibold text-[#5C544F] text-center">{idx + 1}</div>
                <div className="col-span-5">
                  <Input
                    type="number" inputMode="decimal" step="1"
                    placeholder={isRTL ? "مل" : "ml"}
                    value={p.ml}
                    onChange={(e) => setPour(idx, "ml", e.target.value)}
                    data-testid={`${testIdPrefix}-pour-ml-${idx}`}
                    className="h-9 rounded-md border-[#2A2421]/15 text-sm"
                  />
                </div>
                <div className="col-span-5">
                  <Input
                    placeholder={isRTL ? "التوقيت (مثل 0:30)" : "Time (e.g., 0:30)"}
                    value={p.at}
                    onChange={(e) => setPour(idx, "at", e.target.value)}
                    data-testid={`${testIdPrefix}-pour-at-${idx}`}
                    className="h-9 rounded-md border-[#2A2421]/15 text-sm"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removePour(idx)}
                  data-testid={`${testIdPrefix}-pour-remove-${idx}`}
                  className="col-span-1 text-[#5C544F] hover:text-[#C86240] text-lg leading-none"
                  aria-label="remove"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-4">
        <Label className="text-xs uppercase tracking-[0.2em] text-[#5C544F]">{t.addRecipe.notes}</Label>
        <Textarea
          rows={2}
          value={params.notes}
          onChange={set("notes")}
          dir={isRTL ? "rtl" : "ltr"}
          data-testid={`${testIdPrefix}-notes`}
          className="mt-2 rounded-lg border-[#2A2421]/15"
        />
      </div>
    </div>
  );
}

const ParamField = ({ label, children }) => (
  <div>
    <Label className="text-[11px] uppercase tracking-[0.18em] text-[#5C544F]">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
